import type { TenantLegalIdentityProjection } from './projections';

export const BRAND_ARTIFACT_ORIGINS = ['statura_system', 'tenant_originated'] as const;
export type BrandArtifactOrigin = (typeof BRAND_ARTIFACT_ORIGINS)[number];

export const VIA_STATURA_CARE_LOCKUP_TEXT = 'via Statura Care' as const;

/**
 * Candidate copy of the current public legal authority. A composed drift check
 * must keep this aligned with packages/utils/src/public-legal.ts before any
 * renderer adopts the contract.
 */
export const STATURA_ARTIFACT_LEGAL_IDENTITY = {
  displayName: 'Statura Care',
  legalName: 'Statura Operations Pty Ltd',
  abn: '70 696 303 269',
  senderOfRecord: {
    name: 'Statura Care',
    email: 'support@statura.care',
  },
  authority: 'packages/utils/src/public-legal.ts',
} as const;

interface StaturaSystemArtifactInput {
  readonly origin: 'statura_system';
  /** Optional operational context; never changes the primary identity. */
  readonly tenantContext?: TenantLegalIdentityProjection | null;
}

interface TenantOriginatedArtifactInput {
  readonly origin: 'tenant_originated';
  /** Required, separately sourced legal projection; brand_config is ineligible. */
  readonly tenant: TenantLegalIdentityProjection;
}

export type BrandArtifactAttributionInput =
  | StaturaSystemArtifactInput
  | TenantOriginatedArtifactInput;

export interface ResolvedBrandArtifactAttribution {
  readonly origin: BrandArtifactOrigin;
  readonly primaryIdentity: {
    readonly displayName: string;
    readonly legalName: string;
    readonly abn: string | null;
    readonly markAccessibleName: string;
  };
  readonly secondaryAttribution: {
    readonly lockupText: typeof VIA_STATURA_CARE_LOCKUP_TEXT;
    readonly accessibleName: typeof VIA_STATURA_CARE_LOCKUP_TEXT;
  } | null;
  readonly senderOfRecord: {
    readonly name: string;
    readonly email: string | null;
  };
  readonly tenantContext: {
    readonly displayName: string;
    readonly legalName: string;
    readonly abn: string | null;
  } | null;
  readonly fallbackText: string;
}

function tenantFacts(tenant: TenantLegalIdentityProjection) {
  return {
    displayName: tenant.displayName,
    legalName: tenant.legalName,
    abn: tenant.abn,
  } as const;
}

/**
 * Resolve primary, secondary, legal and accessible identity from artifact
 * origin. Unknown origins fail closed instead of guessing from tenant_id.
 */
export function resolveBrandArtifactAttribution(
  input: BrandArtifactAttributionInput,
): ResolvedBrandArtifactAttribution {
  if (input.origin === 'statura_system') {
    return {
      origin: input.origin,
      primaryIdentity: {
        displayName: STATURA_ARTIFACT_LEGAL_IDENTITY.displayName,
        legalName: STATURA_ARTIFACT_LEGAL_IDENTITY.legalName,
        abn: STATURA_ARTIFACT_LEGAL_IDENTITY.abn,
        markAccessibleName: 'Statura Care',
      },
      secondaryAttribution: null,
      senderOfRecord: STATURA_ARTIFACT_LEGAL_IDENTITY.senderOfRecord,
      tenantContext: input.tenantContext ? tenantFacts(input.tenantContext) : null,
      fallbackText: 'Statura Care',
    };
  }

  if (input.origin === 'tenant_originated') {
    if (
      !input.tenant?.displayName ||
      !input.tenant.legalName ||
      !input.tenant.senderOfRecord.name
    ) {
      throw new Error('brand_artifact_tenant_legal_identity_required');
    }

    return {
      origin: input.origin,
      primaryIdentity: {
        ...tenantFacts(input.tenant),
        markAccessibleName: input.tenant.displayName,
      },
      secondaryAttribution: {
        lockupText: VIA_STATURA_CARE_LOCKUP_TEXT,
        accessibleName: VIA_STATURA_CARE_LOCKUP_TEXT,
      },
      senderOfRecord: input.tenant.senderOfRecord,
      tenantContext: tenantFacts(input.tenant),
      fallbackText: `${input.tenant.displayName} ${VIA_STATURA_CARE_LOCKUP_TEXT}`,
    };
  }

  throw new Error('brand_artifact_origin_unknown');
}
